import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import MonetizeModal from '../components/MonetizeModal';
import './ProjectView.css';

function ProjectView() {
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('render');
  const [selectedFile, setSelectedFile] = useState('html');
  const [isMonetizeModalOpen, setMonetizeModalOpen] = useState(false);
  const { projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      if (!user || !projectId) {
        navigate('/');
        return;
      }

      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        
        if (!projectDoc.exists() || projectDoc.data().userId !== user.uid) {
          navigate('/');
          return;
        }

        setProject({
          id: projectDoc.id,
          ...projectDoc.data(),
          createdAt: new Date(projectDoc.data().createdAt).toLocaleDateString(),
          lastModified: new Date(projectDoc.data().lastModified).toLocaleDateString()
        });
      } catch (error) {
        console.error('Error fetching project:', error);
        navigate('/');
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchProject();
  }, [projectId, user, navigate]);

  // Compute a blob URL for the rendered view using useMemo
  const renderSrc = useMemo(() => {
    if (project?.code && (project.code.html || project.code.css || project.code.js)) {
      let cssURL = '';
      if (project.code.css) {
        const cssBlob = new Blob([project.code.css], { type: 'text/css' });
        cssURL = URL.createObjectURL(cssBlob);
      }
      const jsContent = project.code.js ? `<script>${project.code.js}</script>` : '';
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${project.title}</title>
            ${cssURL ? `<link rel="stylesheet" href="${cssURL}">` : ''}
          </head>
          <body>
            ${project.code.html || ''}
            ${jsContent}
          </body>
        </html>
      `;
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      return URL.createObjectURL(htmlBlob);
    }
    return null;
  }, [project]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const userMessage = { text: newMessage, sender: 'user' };
    setProject(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage]
    }));
    setNewMessage('');
    setIsChatLoading(true);

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: "You are a helpful AI that generates HTML/CSS code. Always respond with a brief explanation followed by the code. The HTML code should be enclosed between [[HTML]] and [[/HTML]] tags, and the CSS code should be enclosed between [[CSS]] and [[/CSS]] tags. The code should be valid HTML/CSS that can be directly rendered in a browser."
            },
            // Include previous messages for context
            ...project.messages.map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text
            })),
            {
              role: "user",
              content: newMessage
            }
          ],
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;
      
      // Extract code from response
      const codeSegments = extractCodeFromResponse(aiResponse);
      
      // Update project state with new message and code
      setProject(prev => ({
        ...prev,
        messages: [...prev.messages, { text: aiResponse, sender: 'bot' }],
        code: {
          html: codeSegments.html || prev.code.html,
          css: codeSegments.css || prev.code.css,
          js: codeSegments.js || prev.code.js
        }
      }));

      // Save updates to Firebase
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        messages: [...project.messages, userMessage, { text: aiResponse, sender: 'bot' }],
        code: {
          html: codeSegments.html || project.code.html,
          css: codeSegments.css || project.code.css,
          js: codeSegments.js || project.code.js
        },
        lastModified: Date.now()
      });

    } catch (error) {
      console.error('Error:', error);
      setProject(prev => ({
        ...prev,
        messages: [...prev.messages, { text: 'Error: Failed to get response', sender: 'bot' }]
      }));
    }

    setIsChatLoading(false);
  };

  // Add the extractCodeFromResponse function
  const extractCodeFromResponse = (text) => {
    const htmlRegex = /\[\[HTML\]\]([\s\S]*?)\[\[\/HTML\]\]/;
    const cssRegex = /\[\[CSS\]\]([\s\S]*?)\[\[\/CSS\]\]/;
    const jsRegex = /\[\[JavaScript\]\]([\s\S]*?)\[\[\/JavaScript\]\]/;
    const htmlMatch = text.match(htmlRegex);
    const cssMatch = text.match(cssRegex);
    const jsMatch = text.match(jsRegex);
    return {
      html: htmlMatch ? htmlMatch[1].trim() : null,
      css: cssMatch ? cssMatch[1].trim() : null,
      js: jsMatch ? jsMatch[1].trim() : null,
    };
  };

  // Add saveChanges function
  const saveChanges = async () => {
    if (!user || !project) return;
    
    setIsSaving(true);
    try {
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        messages: project.messages,
        code: project.code,
        lastModified: Date.now()
      });
      // Show brief success message
      alert('Changes saved successfully!');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes. Please try again.');
    }
    setIsSaving(false);
  };

  if (isInitialLoading) {
    return <div className="loading-spinner">Loading project...</div>;
  }

  if (!project) {
    return null;
  }

  return (
    <div className="project-view">
      <div className="project-header">
        <h1>{project.title}</h1>
        {project.description && <p className="description">{project.description}</p>}
        <div className="meta-info">
          <span>Created: {project.createdAt}</span>
          <span>Last Modified: {project.lastModified}</span>
          <button 
            className="monetize-button"
            onClick={() => setMonetizeModalOpen(true)}
          >
            Add to Marketplace
          </button>
        </div>
      </div>

      <div className="project-content">
        <div className="chat-section">
          <div className="chat-header">
            <h2>Chat History</h2>
            <div className="chat-controls">
              {isEditMode && (
                <button 
                  className="save-changes-button"
                  onClick={saveChanges}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
              <button 
                className="reopen-chat-button"
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? 'Close Chat' : 'Reopen Chat'}
              </button>
            </div>
          </div>
          <div className="messages">
            {project.messages.map((message, index) => (
              <div key={index} className={`message ${message.sender}`}>
                <pre>{message.text}</pre>
              </div>
            ))}
            {isChatLoading && (
              <div className="message bot loading-message">
                <div className="loading">Thinking...</div>
              </div>
            )}
          </div>
          {isEditMode && (
            <form onSubmit={handleSendMessage} className="message-input">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Describe what changes you want to make..."
              />
              <button type="submit" disabled={isChatLoading}>
                Send
              </button>
            </form>
          )}
        </div>

        <div className="preview-section">
          <div className="view-toggle-buttons">
            <button
              className={viewMode === 'render' ? 'active' : ''}
              onClick={() => setViewMode('render')}
            >
              Render
            </button>
            <button
              className={viewMode === 'code' ? 'active' : ''}
              onClick={() => setViewMode('code')}
            >
              Code
            </button>
          </div>

          {viewMode === 'render' ? (
            renderSrc ? (
              <iframe
                src={renderSrc}
                className="preview-frame"
                title="Preview"
              />
            ) : (
              <div className="no-preview">No preview available</div>
            )
          ) : (
            <div className="code-view">
              <div className="code-tabs">
                {project.code.html && (
                  <button
                    className={selectedFile === 'html' ? 'active' : ''}
                    onClick={() => setSelectedFile('html')}
                  >
                    HTML
                  </button>
                )}
                {project.code.css && (
                  <button
                    className={selectedFile === 'css' ? 'active' : ''}
                    onClick={() => setSelectedFile('css')}
                  >
                    CSS
                  </button>
                )}
                {project.code.js && (
                  <button
                    className={selectedFile === 'js' ? 'active' : ''}
                    onClick={() => setSelectedFile('js')}
                  >
                    JavaScript
                  </button>
                )}
              </div>
              <div className="code-content">
                <pre>
                  {selectedFile === 'html'
                    ? project.code.html
                    : selectedFile === 'css'
                    ? project.code.css
                    : project.code.js}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Monetize Modal */}
      <MonetizeModal
        isOpen={isMonetizeModalOpen}
        onClose={() => setMonetizeModalOpen(false)}
        projectTitle={project?.title || 'Untitled Project'}
        codeSegments={project?.code}
      />
    </div>
  );
}

export default ProjectView; 